import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { axiosInstance } from "../../utils/networking";
import { StyledButtonMain } from "../common/Buttons";
import FilePreviewDialog, {
  CaseDocRef,
  downloadCaseDoc,
} from "./FilePreviewDialog";

const PREVIEW_COUNT = 3;

// Style for a file name that opens the in-app preview dialog.
const fileNameLinkSx = {
  color: "primary.main",
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

// Builds the preview/download reference from an "Upload File" activity.
const docRefOf = (a: any): CaseDocRef | null => {
  const { storageName, originalName, case_id } = a?.activity_change || {};
  if (!storageName || !originalName || !case_id) return null;
  return { storageName, originalName, case_id };
};

interface CaseFileManagerProps {
  caseID: string;
  activity: any[];
  dataFlag: boolean;
  setDataFlag: (flag: boolean) => void;
  setCardOpen?: (open: boolean) => void;
}

const FileTable: React.FC<{
  files: any[];
  onView: (a: any) => void;
  onDownload: (a: any) => void;
  onDelete?: (a: any) => void;
  onRestore?: (a: any) => void;
}> = ({ files, onView, onDownload, onDelete, onRestore }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>File Name</TableCell>
          <TableCell>Uploaded</TableCell>
          <TableCell align="right"></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {files.map((a: any) => {
          const originalName =
            a.activity_change?.originalName || a.activity_note || "file";
          const uploadedDate = a.created_at
            ? new Date(a.created_at).toLocaleDateString()
            : "";
          return (
            <TableRow key={a.activity_id || a._id}>
              <TableCell>
                <Typography
                  component="span"
                  variant="body2"
                  role="button"
                  tabIndex={0}
                  sx={fileNameLinkSx}
                  title="Preview file"
                  onClick={() => onView(a)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onView(a);
                    }
                  }}
                >
                  {originalName}
                </Typography>
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {uploadedDate}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <IconButton size="small" onClick={() => onView(a)} title="Preview">
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDownload(a)}
                  title="Download"
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
                {onDelete && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(a)}
                    title="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
                {onRestore && (
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onRestore(a)}
                    title="Restore"
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const CaseFileManager: React.FC<CaseFileManagerProps> = ({
  caseID,
  activity,
  dataFlag,
  setDataFlag,
  setCardOpen,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogFileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUploadRef, setPendingUploadRef] = useState<React.RefObject<HTMLInputElement> | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [fileSearchQuery, setFileSearchQuery] = useState("");
  const [fileSearchResults, setFileSearchResults] = useState<any[] | null>(null);
  const [fileSearchLoading, setFileSearchLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<CaseDocRef | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFileSearch = useCallback(
    (query: string) => {
      setFileSearchQuery(query);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (query.trim().length < 2) {
        setFileSearchResults(null);
        return;
      }
      searchTimeoutRef.current = setTimeout(async () => {
        setFileSearchLoading(true);
        try {
          const res = await axiosInstance.get(
            `/search/case-files?case_id=${caseID}&q=${encodeURIComponent(query.trim())}`
          );
          setFileSearchResults(res.data.results);
        } catch {
          setFileSearchResults([]);
        } finally {
          setFileSearchLoading(false);
        }
      }, 300);
    },
    [caseID]
  );

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const activeFiles = activity.filter(
    (a: any) => a.activity_type === "Upload File" && a.status !== "deleted",
  );

  const archivedFiles = activity.filter(
    (a: any) => a.activity_type === "Delete File",
  );

  const doUpload = async (
    file: File,
    inputRef: React.RefObject<HTMLInputElement>,
    chosenName: string,
  ) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await axiosInstance.post(
        `/upload/case-doc?case_id=${caseID}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const { storageName, case_id } = uploadRes.data;

      await axiosInstance.post("/activity/", {
        activity_type: "Upload File",
        case_id: caseID,
        activity_note: chosenName,
        activity_change: { storageName, originalName: chosenName, case_id },
      });

      setDataFlag(!dataFlag);
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleUpload =
    (inputRef: React.RefObject<HTMLInputElement>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const dotIndex = file.name.lastIndexOf(".");
      const baseName = dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name;
      setPendingFile(file);
      setPendingUploadRef(inputRef);
      setDisplayName(baseName);
      setRenameDialogOpen(true);
    };

  const handleRenameConfirm = () => {
    if (pendingFile && pendingUploadRef) {
      const dotIndex = pendingFile.name.lastIndexOf(".");
      const ext = dotIndex > 0 ? pendingFile.name.slice(dotIndex) : "";
      const chosenName = (displayName.trim() || pendingFile.name.slice(0, dotIndex || undefined)) + ext;
      doUpload(pendingFile, pendingUploadRef, chosenName);
    }
    setRenameDialogOpen(false);
    setPendingFile(null);
    setPendingUploadRef(null);
  };

  const handleRenameCancel = () => {
    if (pendingUploadRef?.current) pendingUploadRef.current.value = "";
    setRenameDialogOpen(false);
    setPendingFile(null);
    setPendingUploadRef(null);
    setDisplayName("");
  };

  const handleDownload = (a: any) => {
    const doc = docRefOf(a);
    if (doc) downloadCaseDoc(doc);
  };

  // Opens the in-app preview dialog instead of downloading the file.
  const handleView = (a: any) => {
    const doc = docRefOf(a);
    if (doc) setPreviewDoc(doc);
  };

  const handleDelete = async (a: any) => {
    const activityId = a.activity_id || a._id;
    try {
      // Soft-delete the original Upload File activity via DELETE endpoint
      await axiosInstance.delete(`/activity/?id=${activityId}`);
      // Create a Delete File activity preserving file metadata
      await axiosInstance.post("/activity/", {
        activity_type: "Delete File",
        case_id: caseID,
        activity_note: a.activity_note,
        activity_change: {
          ...a.activity_change,
          original_activity_id: activityId,
        },
      });
      setDataFlag(!dataFlag);
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const handleRestore = async (a: any) => {
    const originalActivityId = a.activity_change?.original_activity_id;
    const deleteActivityId = a.activity_id || a._id;
    try {
      // Restore the original Upload File activity — POST upsert hardcodes status:"active"
      if (originalActivityId) {
        await axiosInstance.post("/activity/", { _id: originalActivityId });
      }
      // Soft-delete the Delete File activity via DELETE endpoint
      await axiosInstance.delete(`/activity/?id=${deleteActivityId}`);
      setDataFlag(!dataFlag);
    } catch (err) {
      console.error("Restore failed", err);
    }
  };

  const previewFiles = activeFiles.slice(0, PREVIEW_COUNT);
  const hasMore = activeFiles.length > PREVIEW_COUNT;

  const renderContent = (
    showAll: boolean,
    uploadRef: React.RefObject<HTMLInputElement>,
  ) => (
    <Box sx={{ padding: setCardOpen || showAll ? "16px" : 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        {!setCardOpen && !showAll && (
          <Typography variant="subtitle1" fontWeight={600}>
            Attached Files
          </Typography>
        )}
        <StyledButtonMain
          size="small"
          variant="contained"
          startIcon={<UploadFileIcon />}
          disabled={uploading}
          onClick={() => uploadRef.current?.click()}
          sx={{ ml: "auto" }}
        >
          {uploading ? "Uploading..." : "Upload"}
        </StyledButtonMain>
        <input
          ref={uploadRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleUpload(uploadRef)}
        />
      </Stack>

      {activeFiles.length > 0 && (
        <TextField
          size="small"
          fullWidth
          placeholder="Search file contents…"
          value={fileSearchQuery}
          onChange={(e) => handleFileSearch(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}

      {fileSearchResults !== null ? (
        fileSearchResults.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {fileSearchLoading ? "Searching…" : "No matching files found."}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fileSearchResults.map((r: any) => {
                  const activity = activeFiles.find(
                    (a: any) => a.activity_change?.storageName === r.storageName
                  );
                  return (
                    <TableRow key={r.storageName} sx={{ backgroundColor: "rgba(158,31,99,0.06)" }}>
                      <TableCell>
                        {activity ? (
                          <Typography
                            component="span"
                            variant="body2"
                            role="button"
                            tabIndex={0}
                            sx={fileNameLinkSx}
                            title="Preview file"
                            onClick={() => handleView(activity)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleView(activity);
                              }
                            }}
                          >
                            {r.originalName}
                          </Typography>
                        ) : (
                          <Typography variant="body2">{r.originalName}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.snippet}
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {activity && (
                          <>
                            <IconButton size="small" onClick={() => handleView(activity)} title="Preview">
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDownload(activity)} title="Download">
                              <FileDownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : activeFiles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No files attached.
        </Typography>
      ) : (
        <>
          <FileTable
            files={showAll ? activeFiles : previewFiles}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
          {!showAll && (hasMore || archivedFiles.length > 0) && (
            <StyledButtonMain
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setDialogOpen(true)}
            >
              More
              {hasMore && ` (${activeFiles.length - PREVIEW_COUNT} more)`}
              {archivedFiles.length > 0 &&
                ` · ${archivedFiles.length} archived`}
            </StyledButtonMain>
          )}
        </>
      )}

      {showAll && archivedFiles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Archived Files
          </Typography>
          <FileTable
            files={archivedFiles}
            onView={handleView}
            onDownload={handleDownload}
            onRestore={handleRestore}
          />
        </>
      )}
    </Box>
  );

  const pendingExt = pendingFile
    ? (() => { const d = pendingFile.name.lastIndexOf("."); return d > 0 ? pendingFile.name.slice(d) : ""; })()
    : "";

  // Rendered by both the inline and the modal (`setCardOpen`) return branches,
  // so the preview dialog is mounted wherever the file tables are.
  const renameDialog = (
    <>
    <FilePreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    <Dialog open={renameDialogOpen} onClose={handleRenameCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Name your file</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleRenameConfirm(); }}
          inputProps={{ onFocus: (e) => e.target.select() }}
          InputProps={pendingExt ? {
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="body2" color="text.secondary">{pendingExt}</Typography>
              </InputAdornment>
            ),
          } : undefined}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRenameCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleRenameConfirm} disabled={!displayName.trim()}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );

  // Modal mode (opened from activity feed click)
  if (setCardOpen) {
    return (
      <>
        {renameDialog}
        <Box
          sx={{
            textAlign: "center",
            backgroundColor: "#9E1F63FF",
            padding: "3px",
            margin: 0,
            color: "white",
            borderRadius: "5px 5px 0 0",
          }}
        >
          <Typography sx={{ display: "inline-block" }} variant="h4">
            Attached Files
          </Typography>
          <Box
            sx={{ display: "inline", float: "right", cursor: "pointer" }}
            onClick={() => setCardOpen(false)}
          >
            <CloseIcon sx={{ fontSize: 40 }} />
          </Box>
        </Box>
        {renderContent(true, fileInputRef)}
      </>
    );
  }

  // Inline mode (full-width section on the case page)
  return (
    <>
      {renameDialog}
      {renderContent(false, fileInputRef)}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            textAlign: "center",
            backgroundColor: "#9E1F63FF",
            padding: "3px",
            color: "white",
            borderRadius: "5px 5px 0 0",
          }}
        >
          <Typography sx={{ display: "inline-block" }} variant="h4">
            Attached Files
          </Typography>
          <Box
            sx={{ display: "inline", float: "right", cursor: "pointer" }}
            onClick={() => setDialogOpen(false)}
          >
            <CloseIcon sx={{ fontSize: 40 }} />
          </Box>
        </Box>
        {renderContent(true, dialogFileInputRef)}
      </Dialog>
    </>
  );
};

export default CaseFileManager;
